<template>
  <!--
    EdgeFinishMenu
    -------------------------------------------------------------------------
    Menu contextual de acabamento de borda, ancorado no ponto do clique.

    MODO CATÁLOGO (preferido): quando o pai passa `catalog` (insumos type
    "Acabamento" cadastrados no Insumos, com imagem e preço), o menu lista
    esses acabamentos reais — escolher um aplica na borda E adiciona o item
    cobrado na ficha (quem faz isso é o orquestrador).

    MODO LEGADO: sem catálogo, mostra os 6 tipos visuais fixos (traço).
  -->
  <div class="efm-root" :style="{ left: `${left}px`, top: `${top}px` }">
    <div class="efm-title">Acabamento · {{ edgeLabel }}</div>

    <!-- catálogo real de acabamentos (com imagem do cadastro) -->
    <template v-if="hasCatalog">
      <button
        v-for="insumo in catalog"
        :key="insumo._id || insumo.nome"
        class="efm-item"
        :class="{ 'efm-item--current': isCurrent(insumo) }"
        @click="$emit('select-insumo', insumo)"
      >
        <img
          v-if="insumoImage(insumo)"
          :src="insumoImage(insumo)"
          class="efm-thumb"
          alt=""
        />
        <span v-else class="efm-thumb efm-thumb--empty">
          <v-icon size="13">mdi-image-off-outline</v-icon>
        </span>
        <span class="efm-item-info">
          <span class="efm-item-name">{{ insumo.nome }}</span>
          <span class="efm-item-price">{{ formatPrice(insumo) }}</span>
        </span>
        <v-icon v-if="isCurrent(insumo)" size="14" color="#128c7e">mdi-check</v-icon>
      </button>
    </template>

    <template v-if="edgeActive">
      <div class="efm-title efm-title--section">Direção do texto</div>
      <div class="efm-direction-grid">
        <button
          v-for="option in directionOptions"
          :key="option.value"
          class="efm-direction"
          :class="{ 'efm-direction--active': currentTextRotation === option.value }"
          :title="option.label"
          @click="$emit('set-text-rotation', option.value)"
        >
          <v-icon size="15">{{ option.icon }}</v-icon>
          <span>{{ option.value }}°</span>
        </button>
      </div>
    </template>

    <!-- sem catálogo: o tipo de acabamento é DEFINIDO pelo cadastro -->
    <div v-else class="efm-empty">
      Nenhum acabamento cadastrado ainda.<br />
      Cadastre em <strong>Insumos → Acabamento</strong> (com uma imagem
      quadrada de como o acabamento é) e ele aparece aqui.
    </div>

    <button v-if="edgeActive" class="efm-item efm-item--remove" @click="$emit('remove-edge')">
      <v-icon size="14" color="#d84343">mdi-close</v-icon>
      <span>Sem acabamento nesta borda</span>
    </button>
  </div>
</template>

<script>
const EDGE_LABELS = {
  front: "Frente",
  back: "Fundo",
  left: "Esquerda",
  right: "Direita"
};

export default {
  name: "EdgeFinishMenu",
  props: {
    left: { type: Number, default: 0 },
    top: { type: Number, default: 0 },
    edge: { type: String, default: "front" },
    // nome do acabamento cadastrado aplicado nesta borda (modo catálogo)
    currentName: { type: String, default: "" },
    // a borda clicada já tem acabamento? (habilita a opção de remover)
    edgeActive: { type: Boolean, default: false },
    currentTextRotation: { type: Number, default: 0 },
    // insumos type "Acabamento" cadastrados (com imagens e preço)
    catalog: { type: Array, default: () => [] }
  },
  computed: {
    directionOptions() {
      return [
        { value: 0, label: "Horizontal", icon: "mdi-format-text" },
        { value: 90, label: "90 graus", icon: "mdi-format-text-rotation-down" },
        { value: 180, label: "Invertido", icon: "mdi-format-text-rotation-down" },
        { value: 270, label: "270 graus", icon: "mdi-format-text-rotation-up" }
      ];
    },
    hasCatalog() {
      return Array.isArray(this.catalog) && this.catalog.length > 0;
    },
    edgeLabel() {
      return EDGE_LABELS[this.edge] || this.edge;
    }
  },
  methods: {
    insumoImage(insumo) {
      if (insumo.imagem) return insumo.imagem;
      if (Array.isArray(insumo.imagens) && insumo.imagens.length) {
        return insumo.imagens[0];
      }
      return "";
    },
    isCurrent(insumo) {
      return !!this.currentName && insumo.nome === this.currentName;
    },
    formatPrice(insumo) {
      const value = parseFloat(
        `${insumo.ultimo_preco_de_compra || 0}`.replace(",", ".")
      ) || 0;
      return value
        ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
        : "";
    }
  }
};
</script>

<style scoped>
.efm-root {
  background: #ffffff;
  border: 1px solid rgba(7, 94, 84, 0.18);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  max-height: 300px;
  min-width: 210px;
  overflow-y: auto;
  padding: 6px;
  position: absolute;
  z-index: 8;
}
.efm-title {
  color: #74827c;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  margin: 2px 6px 6px;
  text-transform: uppercase;
}
.efm-title--section {
  border-top: 1px solid rgba(7, 94, 84, 0.1);
  margin-top: 6px;
  padding-top: 7px;
}
.efm-direction-grid {
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(4, 1fr);
  padding: 0 5px 5px;
}
.efm-direction {
  align-items: center;
  background: #f5f7f6;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #445248;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-size: 9px;
  padding: 4px 2px;
}
.efm-direction--active {
  background: rgba(18, 140, 126, 0.1);
  border-color: #128c7e;
  color: #075e54;
}
.efm-item {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #173b33;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: 8px;
  padding: 5px 8px;
  text-align: left;
  width: 100%;
}
.efm-item:hover {
  background: #f3f8f5;
}
.efm-item--current {
  background: rgba(18, 140, 126, 0.08);
  font-weight: 700;
}
.efm-item--remove {
  border-top: 1px solid rgba(7, 94, 84, 0.1);
  color: #d84343;
  margin-top: 4px;
}
.efm-empty {
  color: #74827c;
  font-size: 11px;
  line-height: 1.4;
  padding: 4px 8px 6px;
}
.efm-thumb {
  border: 1px solid rgba(7, 94, 84, 0.16);
  border-radius: 4px;
  flex-shrink: 0;
  height: 26px;
  object-fit: cover;
  width: 26px;
}
.efm-thumb--empty {
  align-items: center;
  background: #f3f8f5;
  display: inline-flex;
  justify-content: center;
}
.efm-item-info {
  display: flex;
  flex: 1;
  flex-direction: column;
  line-height: 1.15;
}
.efm-item-name {
  font-weight: 600;
}
.efm-item-price {
  color: #74827c;
  font-size: 10px;
}
.efm-item span {
  flex: 1;
}
.efm-item .efm-item-info span {
  flex: initial;
}
</style>
